import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { recurringExpenseService } from '../services/api';
import { useInvoice } from '../context/InvoiceContext';
import { useConfirm } from '../context/ConfirmContext';
import RecurringExpenseModal from './RecurringExpenseModal';
import { formatCurrency } from '../utils/formatCurrency';

const FREQUENCY_LABELS = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half Yearly',
  yearly: 'Yearly'
};

const RecurringExpenseList = forwardRef((props, ref) => {
  const { showNotification } = useInvoice();
  const confirm = useConfirm();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  const loadExpenses = async () => {
    try {
      const data = await recurringExpenseService.getAll();
      setExpenses(data);
    } catch (error) {
      showNotification(error.message || 'Failed to load recurring expenses', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpenses();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({
    refresh: loadExpenses
  }));

  const handleSave = async (formData, id) => {
    try {
      if (id) {
        const updated = await recurringExpenseService.update(id, formData);
        setExpenses(expenses.map(e => e.id === id ? updated : e));
        showNotification('Recurring expense updated', 'success');
      } else {
        const created = await recurringExpenseService.create(formData);
        setExpenses([...expenses, created]);
        showNotification('Recurring expense created', 'success');
      }
      return true;
    } catch (error) {
      showNotification(error.message || 'Failed to save recurring expense', 'error');
      return false;
    }
  };

  const handleToggleActive = async (expense) => {
    try {
      const updated = await recurringExpenseService.update(expense.id, { isActive: !expense.isActive });
      setExpenses(expenses.map(e => e.id === expense.id ? updated : e));
      showNotification(`Recurring expense ${updated.isActive ? 'activated' : 'paused'}`, 'success');
    } catch (error) {
      showNotification(error.message || 'Failed to update', 'error');
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm('Are you sure you want to delete this recurring expense? This cannot be undone.', {
      title: 'Delete Recurring Expense',
      confirmLabel: 'Delete',
      danger: true
    });
    if (!ok) return;
    try {
      await recurringExpenseService.delete(id);
      setExpenses(expenses.filter(e => e.id !== id));
      showNotification('Recurring expense deleted', 'success');
    } catch (error) {
      showNotification(error.message || 'Failed to delete', 'error');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingExpense(null);
  };

  if (loading) return null;

  return (
    <div className="card mb-lg">
      <div
        className="flex-between"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
          <h3 style={{ margin: 0 }}>Recurring Expenses</h3>
          <span className="badge badge-primary">{expenses.length}</span>
        </div>
        <span style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-text-secondary)' }}>
          {expanded ? '▲' : '▼'}
        </span>
      </div>

      {expanded && expenses.length > 0 && (
        <div style={{ marginTop: 'var(--spacing-md)', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Vendor</th>
                <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Amount</th>
                <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Frequency</th>
                <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>From Date</th>
                <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Status</th>
                <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map(expense => (
                <tr key={expense.id} style={{ borderBottom: '1px solid var(--color-gray-200)' }}>
                  <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)' }}>
                    {expense.vendorName}
                  </td>
                  <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', textAlign: 'right' }}>
                    {formatCurrency(expense.amount)}
                  </td>
                  <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)' }}>
                    <span className="badge badge-primary">{FREQUENCY_LABELS[expense.frequency]}</span>
                  </td>
                  <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)' }}>
                    {new Date(expense.startDate).toLocaleDateString('en-GB')}
                  </td>
                  <td style={{ padding: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)' }}>
                    <span className={`badge ${expense.isActive ? 'badge-approved' : 'badge-pending'}`}>
                      {expense.isActive ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }}>
                    <div className="flex gap-md" style={{ justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleToggleActive(expense)}
                        className={`btn btn-sm ${expense.isActive ? 'btn-secondary' : 'btn-success'}`}
                      >
                        {expense.isActive ? 'Pause' : 'Resume'}
                      </button>
                      <button onClick={() => handleEdit(expense)} className="btn btn-secondary btn-sm">
                        Edit
                      </button>
                      <button onClick={() => handleDelete(expense.id)} className="btn btn-error btn-sm">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {expanded && expenses.length === 0 && (
        <div style={{ marginTop: 'var(--spacing-md)', textAlign: 'center', color: 'var(--color-text-secondary)', padding: 'var(--spacing-lg)' }}>
          No recurring expenses yet
        </div>
      )}

      {showModal && (
        <RecurringExpenseModal
          onClose={handleCloseModal}
          onSave={handleSave}
          expense={editingExpense}
        />
      )}
    </div>
  );
});

export default RecurringExpenseList;
